import { useState } from "react";
import { uloopApi } from "api/uloop";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "providers/Layout";
import { useAuthContext } from "providers/Auth";
import { Card } from "shared/components/Card";
import { ConfirmDialog } from "shared/components/modals/ConfirmDialog";
import { IntegrationId } from "views/settings-integration/components/AddIntegrationModal/types";
import { FeedAutoAddCard } from "views/settings-integration/integrations/shared/FeedAutoAddCard";
import { FeedListingsCard } from "views/settings-integration/integrations/shared/FeedListingsCard";
import { IntegrationDetailHeader } from "views/settings-integration/integrations/shared/IntegrationDetailHeader";
import { UloopPricingModel } from "views/settings-integration/integrations/shared/UloopPricingModel";
import { invalidateFeedQueries } from "views/settings-integration/integrations/shared/useSyndicationFeed";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";

const QUERY_ULOOP_STATUS = ["ULOOP", "STATUS"] as const;

export const UloopView = () => {
  const navigate = useNavigate();
  const { refreshPartnerData, partner } = useAuthContext();
  const queryClient = useQueryClient();

  const { data: status } = useQuery({
    queryKey: QUERY_ULOOP_STATUS,
    queryFn: uloopApi.getStatus,
    placeholderData: partner?.uloop_connected
      ? {
          connected: true,
          featured_opted_in: Boolean(partner.uloop_featured_opted_in),
          auto_add_listings: true
        }
      : undefined
  });

  const [confirmingFeatured, setConfirmingFeatured] = useState(false);

  const { mutate: disconnectUloop, isPending: isDisconnecting } = useMutation({
    mutationKey: ["ULOOP", "DELETE"],
    mutationFn: () => uloopApi.disconnect(),
    onSuccess: () => {
      toast.success("Uloop integration disconnected successfully");
      refreshPartnerData();
      navigate("/settings/integration");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: updateFeatured, isPending: isUpdatingFeatured } = useMutation(
    {
      mutationKey: ["ULOOP", "UPDATE"],
      mutationFn: (featured_opted_in: boolean) =>
        uloopApi.update({ featured_opted_in }),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: QUERY_ULOOP_STATUS });
        invalidateFeedQueries(queryClient, "uloop");
        refreshPartnerData();
        setConfirmingFeatured(false);
        toast.success("Uloop pricing model updated");
      },
      onError: (err) => {
        setConfirmingFeatured(false);
        toast.error(getErrorMessage(err));
      }
    }
  );

  const featuredOptedIn = Boolean(status?.featured_opted_in);

  // Switching to Featured commits $250/mo per property, so it asks first.
  const handlePricingChange = (featured: boolean) => {
    if (featured === featuredOptedIn) return;
    if (featured) setConfirmingFeatured(true);
    else updateFeatured(false);
  };

  return (
    <Layout
      header={
        <IntegrationDetailHeader
          id={IntegrationId.ULOOP}
          name="Uloop"
          path="/settings/integration/uloop"
          onDisconnect={() => disconnectUloop()}
          isDisconnecting={isDisconnecting}
        />
      }
    >
      <div className="space-y-6">
        <Card className="p-6">
          <UloopPricingModel
            label="Pricing model"
            featuredOptedIn={featuredOptedIn}
            onChange={handlePricingChange}
            disabled={isUpdatingFeatured}
          />
        </Card>

        <ConfirmDialog
          opened={confirmingFeatured}
          variant="primary"
          title="Enable Featured pricing?"
          description={
            <>
              Uloop will charge <b>$250 per month</b> for each property you opt
              into the Featured tier. This fee is charged directly by Uloop, not
              Boom.
            </>
          }
          confirmButton={{
            label: "Enable Featured",
            loading: isUpdatingFeatured
          }}
          cancelButton={{ label: "Cancel", disabled: isUpdatingFeatured }}
          onClose={() => setConfirmingFeatured(false)}
          onConfirm={() => updateFeatured(true)}
        />

        <FeedAutoAddCard feed="uloop" feedLabel="Uloop" />
        <FeedListingsCard feed="uloop" feedLabel="Uloop" />
      </div>
    </Layout>
  );
};
